import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import ConfirmDialog from './ConfirmDialog';
import { Copy, Share2, Trash2 } from "lucide-react";

import { API } from "../App";
import { useT } from "../i18n";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

/*
  Workspace-side widget for managing public read-only dashboard links
  (audit Phase 2 §6.2.A). Mounted inside CampaignDetail for the
  associated campaign — operators can:

  * mint a new token-gated link (plaintext shown exactly once)
  * see existing links + view counts + expiry
  * revoke an active link

  The component is intentionally noisy on the create-once-token UX
  because the plaintext is unrecoverable: if the operator clicks past
  the toast, they have to revoke + remint to re-share.
*/

const ALL_METRICS = [
  { key: "total_clicks", label: "Total clicks" },
  { key: "unique_clicks", label: "Unique clicks" },
  { key: "conversions", label: "Conversions" },
  { key: "conversion_rate", label: "Conversion rate" },
  { key: "clicks_by_country", label: "Country breakdown" },
  { key: "clicks_by_device", label: "Device breakdown" },
  { key: "clicks_by_browser", label: "Browser breakdown" },
  { key: "revenue_total", label: "Revenue (sensitive)" },
  { key: "cost_total", label: "Cost (sensitive)" },
  { key: "epc", label: "EPC (sensitive)" },
  { key: "roi", label: "ROI (sensitive)" },
];

const DEFAULT_METRICS = ["total_clicks", "unique_clicks", "conversions", "conversion_rate"];

function formatDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
}

export default function PublicDashboardLinks({ campaignId }) {
  const { t } = useT();
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [label, setLabel] = useState("");
  const [expiryDays, setExpiryDays] = useState(30);
  const [allowedMetrics, setAllowedMetrics] = useState(DEFAULT_METRICS);
  const [creating, setCreating] = useState(false);
  const [createdToken, setCreatedToken] = useState(null);
  const [createdUrl, setCreatedUrl] = useState(null);

  const baseOrigin = useMemo(() => {
    if (typeof window === "undefined") return "";
    return window.location.origin;
  }, []);

  const fetchLinks = useCallback(async () => {
    if (!campaignId) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/public-dashboard-links`, {
        params: { campaign_id: campaignId },
      });
      setLinks(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      // Surfacing every list-load failure as a toast is too noisy when
      // the user lacks the role; the empty state is honest enough.
      setLinks([]);
    } finally {
      setLoading(false);
    }
  }, [campaignId]);

  useEffect(() => {
    fetchLinks();
  }, [fetchLinks]);

  const toggleMetric = (key) => {
    setAllowedMetrics((prev) =>
      prev.includes(key) ? prev.filter((m) => m !== key) : [...prev, key],
    );
  };

  const handleCreate = async () => {
    if (!campaignId) return;
    setCreating(true);
    try {
      const response = await axios.post(`${API}/public-dashboard-links`, {
        campaign_id: campaignId,
        label: label || null,
        allowed_metrics: allowedMetrics,
        expiry_days: Number(expiryDays) || 30,
      });
      const token = response.data?.token;
      const path = response.data?.public_url_path || `/public/dashboard/${token}`;
      setCreatedToken(token);
      setCreatedUrl(`${baseOrigin}${path}`);
      toast.success(t("Public link created — copy it now, it won't be shown again."));
      fetchLinks();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "unknown error";
      toast.error(`${t("Could not create public link")}: ${detail}`);
    } finally {
      setCreating(false);
    }
  };

  const [revokeTarget, setRevokeTarget] = useState(null);

  const handleRevoke = async (linkId) => {
    if (!linkId) return;
    try {
      await axios.delete(`${API}/public-dashboard-links/${linkId}`);
      setRevokeTarget(null);
      toast.success(t("Public link revoked."));
      fetchLinks();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "unknown error";
      toast.error(`${t("Revoke failed")}: ${detail}`);
    }
  };

  const handleCopy = async (text) => {
    if (!text) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        toast.success(t("Copied to clipboard."));
      }
    } catch {
      toast.error(t("Could not copy — please copy the link manually."));
    }
  };

  const resetCreateState = () => {
    setLabel("");
    setExpiryDays(30);
    setAllowedMetrics(DEFAULT_METRICS);
    setCreatedToken(null);
    setCreatedUrl(null);
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Share2 className="h-4 w-4" />
            {t("Public read-only links")}
          </CardTitle>
          <CardDescription>
            {t("Share a token-gated dashboard URL with advertisers without giving them an account.")}
          </CardDescription>
        </div>
        <Dialog
          open={createOpen}
          onOpenChange={(open) => {
            setCreateOpen(open);
            if (!open) resetCreateState();
          }}
        >
          <DialogTrigger asChild>
            <Button size="sm">{t("Create link")}</Button>
          </DialogTrigger>
          <DialogContent className="max-w-xl">
            <DialogHeader>
              <DialogTitle>{t("New public dashboard link")}</DialogTitle>
              <DialogDescription>
                {t("The token will only be shown once after creation — copy it immediately.")}
              </DialogDescription>
            </DialogHeader>

            {createdToken ? (
              <div className="space-y-3">
                <Label>{t("Shareable URL")}</Label>
                <div className="flex items-center gap-2">
                  <Input readOnly value={createdUrl || ""} />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => handleCopy(createdUrl)}
                    aria-label={t("Copy")}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  {t("This link is unauthenticated. Anyone with it can view the selected metrics until it expires or is revoked.")}
                </p>
                <DialogFooter>
                  <Button onClick={() => setCreateOpen(false)}>{t("Done")}</Button>
                </DialogFooter>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>{t("Label (operator-only)")}</Label>
                  <Input
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    placeholder="ClientCo Q4"
                  />
                </div>

                <div className="space-y-2">
                  <Label>{t("Expires after (days)")}</Label>
                  <Input
                    type="number"
                    min="1"
                    max="365"
                    value={expiryDays}
                    onChange={(e) => setExpiryDays(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label>{t("Visible metrics")}</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {ALL_METRICS.map((metric) => (
                      <label
                        key={metric.key}
                        className="flex cursor-pointer items-center gap-2 rounded border border-border p-2 text-sm"
                      >
                        <input
                          type="checkbox"
                          checked={allowedMetrics.includes(metric.key)}
                          onChange={() => toggleMetric(metric.key)}
                        />
                        <span>{t(metric.label)}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <DialogFooter>
                  <Button variant="outline" onClick={() => setCreateOpen(false)}>
                    {t("Cancel")}
                  </Button>
                  <Button onClick={handleCreate} disabled={creating}>
                    {creating ? t("Creating…") : t("Create")}
                  </Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </CardHeader>

      <CardContent className="space-y-3">
        {loading && (
          <div className="text-sm text-muted-foreground">{t("Loading…")}</div>
        )}
        {!loading && links.length === 0 ? (
          <div className="rounded border border-dashed border-border p-4 text-sm text-muted-foreground">
            {t("No public links yet. Create one to share read-only stats with advertisers.")}
          </div>
        ) : null}
        {!loading &&
          links.map((link) => (
            <div
              key={link.id}
              className="flex items-center justify-between rounded border border-border bg-card p-3"
            >
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-sm font-medium">
                  {link.label || t("(no label)")}
                  {link.is_revoked ? (
                    <Badge variant="destructive">{t("revoked")}</Badge>
                  ) : null}
                </div>
                <div className="text-xs text-muted-foreground">
                  {t("Expires")}: {formatDate(link.expires_at)} · {t("Views")}: {link.view_count}
                  {link.last_viewed_at
                    ? ` · ${t("Last viewed")}: ${formatDate(link.last_viewed_at)}`
                    : ""}
                </div>
                <div className="text-xs text-muted-foreground">
                  {t("Allowed metrics")}: {link.allowed_metrics.join(", ")}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {!link.is_revoked && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setRevokeTarget(link)}
                  >
                    <Trash2 className="mr-1 h-3 w-3" />
                    {t("Revoke")}
                  </Button>
                )}
              </div>
            </div>
          ))}
      </CardContent>

      <ConfirmDialog
        open={!!revokeTarget}
        onOpenChange={(open) => { if (!open) setRevokeTarget(null); }}
        title={t('Revoke public link')}
        description={t('Anyone with this link will lose access to the shared dashboard immediately.')}
        confirmLabel={t('Revoke')}
        onConfirm={() => handleRevoke(revokeTarget?.id)}
      />
    </Card>
  );
}
